import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function ManageMilk() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [cows, setCows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [form, setForm] = useState({ cow: '', periodType: 'daily', date: new Date().toISOString().split('T')[0], startDate: '', endDate: '', morningLiters: '', eveningLiters: '', pricePerLiter: 60, soldLiters: '', usedForPuja: '' });

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams(filter);
      const [milkRes, cowsRes] = await Promise.all([API.get(`/admin/milk-records?${q}`), API.get('/cows')]);
      setRecords(milkRes.data.records);
      setSummary(milkRes.data.summary);
      setCows(cowsRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchRecords(); }, [filter]);

  const periodDays = form.periodType !== 'daily' && form.startDate && form.endDate
    ? Math.max(Math.round((new Date(form.endDate) - new Date(form.startDate)) / (1000 * 60 * 60 * 24)) + 1, 1)
    : 1;
  const estimatedTotalLiters = ((parseFloat(form.morningLiters) || 0) + (parseFloat(form.eveningLiters) || 0)) * periodDays;

  const handleAdd = async () => {
    if (!form.morningLiters && !form.eveningLiters) return toast.error('Enter at least one milk reading');
    if (form.periodType !== 'daily') {
      if (!form.startDate || !form.endDate) return toast.error('Enter both start and end date');
      if (form.endDate < form.startDate) return toast.error('End date cannot be before start date');
    }
    const payload = form.periodType === 'daily'
      ? { ...form, startDate: undefined, endDate: undefined }
      : { ...form, date: form.startDate };
    try {
      await API.post('/admin/milk-records', payload);
      toast.success('Milk record added');
      setShowForm(false);
      fetchRecords();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="admin-page-title" style={{ margin: 0 }}>🥛 Milk Records</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>+ Add Record</button>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <select className="form-select" style={{ width: 130 }} value={filter.month} onChange={e => setFilter(p => ({ ...p, month: e.target.value }))}>
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select className="form-select" style={{ width: 110 }} value={filter.year} onChange={e => setFilter(p => ({ ...p, year: e.target.value }))}>
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: '16px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '3px solid #DAA520' }}>
          <div style={{ fontSize: '0.85rem', color: '#888' }}>Total Milk (Month)</div>
          <div style={{ fontWeight: 700, fontSize: '1.5rem', color: '#DAA520' }}>{summary.totalLiters || 0} L</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 10, padding: '16px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '3px solid var(--green)' }}>
          <div style={{ fontSize: '0.85rem', color: '#888' }}>Milk Revenue (Month)</div>
          <div style={{ fontWeight: 700, fontSize: '1.5rem', color: 'var(--green)' }}>₹{(summary.totalRevenue || 0).toLocaleString('en-IN')}</div>
        </div>
      </div>

      {showForm && (
        <div className="admin-form-card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>Add Milk Record</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Cow</label>
              <select className="form-select" value={form.cow} onChange={e => setForm(p => ({ ...p, cow: e.target.value }))}>
                <option value="">All / Collective</option>
                {cows.map(c => <option key={c._id} value={c._id}>{c.name} ({c.breed})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Period</label>
              <select className="form-select" value={form.periodType} onChange={e => setForm(p => ({ ...p, periodType: e.target.value }))}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
          {form.periodType === 'daily' ? (
            <div className="form-group">
              <label className="form-label">Date</label>
              <input className="form-input" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
            </div>
          ) : (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input className="form-input" type="date" value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input className="form-input" type="date" value={form.endDate} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))} />
              </div>
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Morning {form.periodType !== 'daily' ? '(Liters/day)' : '(Liters)'}</label>
              <input className="form-input" type="number" step="0.1" value={form.morningLiters} onChange={e => setForm(p => ({ ...p, morningLiters: e.target.value }))} placeholder="0.0" />
            </div>
            <div className="form-group">
              <label className="form-label">Evening {form.periodType !== 'daily' ? '(Liters/day)' : '(Liters)'}</label>
              <input className="form-input" type="number" step="0.1" value={form.eveningLiters} onChange={e => setForm(p => ({ ...p, eveningLiters: e.target.value }))} placeholder="0.0" />
            </div>
          </div>
          {form.periodType !== 'daily' && (
            <p style={{ fontSize: '0.82rem', color: '#888', marginTop: -12, marginBottom: 20 }}>
              {periodDays} day{periodDays === 1 ? '' : 's'} × {((parseFloat(form.morningLiters) || 0) + (parseFloat(form.eveningLiters) || 0)).toFixed(1)} L/day = <strong>{estimatedTotalLiters.toFixed(1)} L</strong> total
              {form.pricePerLiter && <> (₹{(estimatedTotalLiters * (parseFloat(form.pricePerLiter) || 0)).toLocaleString('en-IN')})</>}
            </p>
          )}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Price Per Liter (₹)</label>
              <input className="form-input" type="number" value={form.pricePerLiter} onChange={e => setForm(p => ({ ...p, pricePerLiter: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Sold (Liters)</label>
              <input className="form-input" type="number" step="0.1" value={form.soldLiters} onChange={e => setForm(p => ({ ...p, soldLiters: e.target.value }))} placeholder="0.0" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Used for Puja (Liters)</label>
            <input className="form-input" type="number" step="0.1" value={form.usedForPuja} onChange={e => setForm(p => ({ ...p, usedForPuja: e.target.value }))} placeholder="0.0" />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleAdd}>Save Record</button>
            <button className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="loader"><div className="spinner" /></div> : (
        <div className="admin-table">
          <table>
            <thead><tr>
              <th>Date</th><th>Cow</th><th>Morning</th><th>Evening</th><th>Total</th><th>Sold</th><th>Puja</th><th>Revenue</th>
            </tr></thead>
            <tbody>
              {records.map(r => (
                <tr key={r._id}>
                  <td>
                    {r.periodType && r.periodType !== 'daily'
                      ? `${new Date(r.startDate).toLocaleDateString('en-IN')} – ${new Date(r.endDate).toLocaleDateString('en-IN')}`
                      : new Date(r.date).toLocaleDateString('en-IN')}
                    {r.periodType && r.periodType !== 'daily' && (
                      <span className="badge badge-brown" style={{ marginLeft: 8, textTransform: 'capitalize' }}>{r.periodType}</span>
                    )}
                  </td>
                  <td>{r.cow?.name || 'All Cows'}</td>
                  <td>{r.morningLiters} L</td>
                  <td>{r.eveningLiters} L</td>
                  <td style={{ fontWeight: 700 }}>{r.totalLiters} L</td>
                  <td>{r.soldLiters} L</td>
                  <td>{r.usedForPuja} L</td>
                  <td style={{ fontWeight: 700, color: 'var(--green)' }}>₹{r.totalAmount?.toLocaleString('en-IN')}</td>
                </tr>
              ))}
              {records.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No records found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
